<div class="py-24 sm:py-32">
  <div class="mx-auto max-w-7xl px-6 lg:px-8">

    <!-- Section Header -->
    <div class="mx-auto max-w-2xl text-center" @fadeIn>
      <h2 class="text-base font-semibold leading-7 text-indigo-600">
        {{ 'support.label' | translate }}
      </h2>
      <p class="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
        {{ 'support.heading' | translate }}
      </p>
      <p class="mt-6 text-lg leading-8 text-gray-600">
        {{ 'support.description' | translate }}
      </p>
    </div>

    <!-- Form -->
    <div class="mx-auto mt-16 max-w-2xl" @formAnimation>
      <form [formGroup]="supportForm" (ngSubmit)="onSubmit()"
        class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <div class="px-6 py-6 sm:p-8">
          <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">

            <!-- Full Name -->
            <div class="sm:col-span-2">
              <label for="fullName" class="block text-sm font-semibold leading-6 text-gray-900">
                {{ 'support.form.full_name' | translate }}
              </label>
              <div class="mt-2.5">
                <input type="text" id="fullName" formControlName="fullName" maxlength="100"
                  class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <div *ngIf="supportForm.get('fullName')?.invalid && supportForm.get('fullName')?.touched"
                  class="mt-1 text-sm text-red-600">
                  {{ 'support.form.validation.full_name_required' | translate }}
                </div>
              </div>
            </div>

            <!-- Mobile -->
            <div>
              <label for="mobile" class="block text-sm font-semibold leading-6 text-gray-900">
                {{ 'support.form.mobile' | translate }}
              </label>
              <div class="mt-2.5">
                <input type="tel" id="mobile" formControlName="mobile" placeholder="+96890000000"
                  class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <div *ngIf="supportForm.get('mobile')?.invalid && supportForm.get('mobile')?.touched"
                  class="mt-1 text-sm text-red-600">
                  {{ 'support.form.validation.mobile_format' | translate }}
                </div>
              </div>
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="block text-sm font-semibold leading-6 text-gray-900">
                {{ 'support.form.email' | translate }}
              </label>
              <div class="mt-2.5">
                <input type="email" id="email" formControlName="email"
                  class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <div *ngIf="supportForm.get('email')?.invalid && supportForm.get('email')?.touched"
                  class="mt-1 text-sm text-red-600">
                  {{ 'support.form.validation.email_required' | translate }}
                </div>
              </div>
            </div>

            <!-- Branch Name -->
            <div>
              <label for="branchName" class="block text-sm font-semibold leading-6 text-gray-900">
                {{ 'support.form.branch_name' | translate }}
              </label>
              <div class="mt-2.5">
                <input type="text" id="branchName" formControlName="branchName" maxlength="100"
                  class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <div *ngIf="supportForm.get('branchName')?.invalid && supportForm.get('branchName')?.touched"
                  class="mt-1 text-sm text-red-600">
                  {{ 'support.form.validation.branch_required' | translate }}
                </div>
              </div>
            </div>

            <!-- Issue Category -->
            <div>
              <label for="zIssueCategoryId" class="block text-sm font-semibold leading-6 text-gray-900">
                {{ 'support.form.issue_category' | translate }}
              </label>
              <div class="mt-2.5">
                <div *ngIf="categoriesLoading" class="flex items-center gap-2 py-2 text-sm text-gray-500">
                  <div class="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-indigo-600"></div>
                  {{ 'support.form.loading_categories' | translate }}
                </div>
                <select *ngIf="!categoriesLoading" id="zIssueCategoryId" formControlName="zIssueCategoryId"
                  class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
                  <option value="">{{ 'support.form.select_category' | translate }}</option>
                  <option *ngFor="let cat of categories" [value]="cat.zIssueCategoryId">
                    {{ getCategoryName(cat) }}
                  </option>
                </select>
                <div *ngIf="supportForm.get('zIssueCategoryId')?.invalid && supportForm.get('zIssueCategoryId')?.touched"
                  class="mt-1 text-sm text-red-600">
                  {{ 'support.form.validation.category_required' | translate }}
                </div>
              </div>
            </div>

            <!-- Subject -->
            <div class="sm:col-span-2">
              <label for="subject" class="block text-sm font-semibold leading-6 text-gray-900">
                {{ 'support.form.subject' | translate }}
              </label>
              <div class="mt-2.5">
                <input type="text" id="subject" formControlName="subject" maxlength="200"
                  class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <div *ngIf="supportForm.get('subject')?.invalid && supportForm.get('subject')?.touched"
                  class="mt-1 text-sm text-red-600">
                  {{ 'support.form.validation.subject_required' | translate }}
                </div>
              </div>
            </div>

            <!-- Issue Description -->
            <div class="sm:col-span-2">
              <label for="issueDesc" class="block text-sm font-semibold leading-6 text-gray-900">
                {{ 'support.form.issue_desc' | translate }}
              </label>
              <div class="mt-2.5">
                <textarea id="issueDesc" formControlName="issueDesc" rows="5" maxlength="2000"
                  class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 resize-none"></textarea>
                <div class="mt-1 flex justify-between">
                  <div *ngIf="supportForm.get('issueDesc')?.invalid && supportForm.get('issueDesc')?.touched"
                    class="text-sm text-red-600">
                    {{ 'support.form.validation.desc_required' | translate }}
                  </div>
                  <span class="text-xs text-gray-400 ml-auto">{{ issueDescLength }}/2000</span>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Submit Button -->
        <div class="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-6 py-4">
          <button type="submit" [disabled]="isSubmitting"
            class="rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 flex items-center gap-2">
            <span *ngIf="isSubmitting" class="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
            <span *ngIf="!isSubmitting">{{ 'support.form.submit' | translate }}</span>
            <span *ngIf="isSubmitting">{{ 'support.form.sending' | translate }}</span>
          </button>
        </div>
      </form>

      <!-- Success Message -->
      <div *ngIf="submitted" class="mt-6 rounded-md bg-green-50 p-4" @fadeIn>
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-green-800">
              {{ 'support.form.success' | translate }}
            </p>
            <p *ngIf="submittedTicketId" class="mt-1 text-xs text-green-700 font-mono">
              {{ 'support.form.ticket_id' | translate }}: {{ submittedTicketId }}
            </p>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div *ngIf="submitError" class="mt-6 rounded-md bg-red-50 p-4" @fadeIn>
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-red-800">
              {{ 'support.form.error' | translate }}
            </p>
          </div>
        </div>
      </div>

    </div>
  </div>
</div>
